import BootstrapTable from 'react-bootstrap-table-next';
import {
  managerUserCheckboxFormatter,
  managerUserDeleteFormatter,
  managerUserNamesFormatter,
  managerUserRoleHeaderFormatter,
  managerUserSinceFormatter,
  managerUserClinicHeaderFormatter,
  calculateMiddleIndex,
} from '../helper';
import '../user-style.css';
import { PaginationFooter } from '../../Table/PaginationFooter';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ConfirmationModal from '../../Modal/ConfirmationModal/ConfirmationModal';
import { useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { useToast } from '../../../../context';
import { usePagination } from '../../Table/PaginationFooter/hooks/usePagination';
import { usePostUpdateUserPermissions } from '../ManagePermissions/hooks/usePostUpdateUserRole';
import { LoadingSpinner } from '../../LoadingSpinner';

// for reference interface
// const mockUsers = [
//   {
//     id: 24,
//     first_name: 'James',
//     last_name: 'Aminoff',
//     created_at: '2022-08-23 13:09:06.047 +0930',
//     clinician_id: '76092',
//     roles: {
//       administrator: false,
//       manager: false,
//       team_leader: false,
//       pharm_assitant: true,
//       pharmacist: false,
//     },
//   },
// ];

const defaultColumns = [
  {
    dataField: 'first_name',
    text: 'User',
    classes: 'col6',
    formatter: managerUserNamesFormatter,
    headerClasses: 'user-normal-header',
    events: {
      onClick: (e, column, columnIndex, row, rowIndex) => {
        e.stopPropagation();
      },
    },
  },
  {
    dataField: 'created_at',
    text: 'User since',
    classes: 'col6',
    formatter: managerUserSinceFormatter,
    headerClasses: 'user-normal-header',
    events: {
      onClick: (e, column, columnIndex, row, rowIndex) => {
        e.stopPropagation();
      },
    },
  },
];

const deleteColumn = [
  {
    dataField: 'id',
    text: '',
    classes: 'col3 text-center',
    formatter: managerUserDeleteFormatter,
    headerClasses: 'user-normal-header',
  },
];

function mapUserRoleFormData(tenancyRoles, allUser, clinics) {
  if (!Array.isArray(allUser)) return [];
  const roleKeys = Object.keys(tenancyRoles);

  return allUser.map((user) => {
    const roles = roleKeys.reduce((acc, cur) => {
      acc[cur] = Array.isArray(user.roles) ? user.roles.includes(cur) : false;
      return acc;
    }, {});
    const userClinics = clinics.reduce((acc, cur) => {
      acc[cur.clinic_id] = Array.isArray(user.clinics)
        ? user.clinics.includes(cur.clinic_id)
        : false;
      return acc;
    }, {});

    return {
      id: user.user_id,
      first_name: user.first_name,
      last_name: user.last_name,
      clinics: userClinics,
      userStatus: user.user_status,
      clinician_id: '',
      roles: roles,
      created_at: user.created_at,
    };
  });
}

export const ManageUserTable = ({ roleResults, isGetting }) => {
  const sort = { dataField: 'id', order: 'asc' };
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const activeId = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const { allUsers, selectedTenancy } = useSelector(
    (state) => state.clinicianProfile,
  );
  const tenancyId = selectedTenancy?.tenancy_id;
  const currentTenancyUsers = allUsers?.[tenancyId] || [];
  const { setToast } = useToast();
  const { postUpdateUserPermissions, isLoading: isUpdating } =
    usePostUpdateUserPermissions();
  const isLoading = isGetting || isUpdating;
  const { allTenancyClinics } = useSelector((state) => state.clinicianProfile);

  const clinics =
    allTenancyClinics
      ?.filter(
        (clinic) =>
          clinic.tenancy_id === selectedTenancy?.tenancy_id &&
          clinic.organisation_unit == 'hospital',
      )
      .map((clinic) => ({
        ...clinic,
        caremapprClinicians: (clinic.caremapprClinicians || []).filter(
          (c) => c?.tenancyId === tenancyId,
        ),
      })) || [];

  const { handleSubmit, watch, setValue, getValues, reset } = useForm({
    defaultValues: {
      userRolesFormData: [],
    },
  });

  const usersData = watch('userRolesFormData');

  const {
    currentPage,
    totalPages,
    pageSize,
    handleNextPage,
    handlePreviousPage,
    setPageSize,
    canNextPage,
    canPreviousPage,
  } = usePagination({
    defaultSize: 10,
    totalCount: Array.isArray(usersData) ? usersData.length : 0,
  });
  const paginatedData = (Array.isArray(usersData) ? usersData : []).slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );

  const initialDataRef = useRef([]);
  const isSubmittingRef = useRef(false);

  useEffect(() => {
    if (!roleResults || !currentTenancyUsers) return;
    if (isSubmittingRef.current || isDirty) return;
    const newData = mapUserRoleFormData(
      roleResults?.tenancy_roles,
      currentTenancyUsers,
      clinics,
    );

    if (JSON.stringify(initialDataRef.current) !== JSON.stringify(newData)) {
      // Keep a deep copy of the original data for diffing on save
      initialDataRef.current = JSON.parse(JSON.stringify(newData));
      reset({ userRolesFormData: newData });
    }
  }, [roleResults, currentTenancyUsers, reset, isDirty]);

  const resetPermissionForm = async () => {
    const newData = mapUserRoleFormData(
      roleResults?.tenancy_roles,
      currentTenancyUsers || [],
      clinics || [],
    );
    reset({
      userRolesFormData: newData,
    });
    // Reset original snapshot
    initialDataRef.current = JSON.parse(JSON.stringify(newData));
    setIsDirty(false);
  };

  const changedItems = useMemo(() => {
    const currentData = getValues('userRolesFormData');
    const originalData = initialDataRef.current || [];
    // Compute changes only
    const changedItems = currentData.filter((item, idx) => {
      const orig = originalData[idx];
      if (!orig) return true;
      const rolesChanged =
        JSON.stringify(item.roles) !== JSON.stringify(orig.roles);
      const clinicsChanged =
        JSON.stringify(item.clinics) !== JSON.stringify(orig.clinics);
      const statusChanged = (item.userStatus ?? 0) !== (orig.userStatus ?? 0);
      return rolesChanged || clinicsChanged || statusChanged;
    });

    if (changedItems.length === 0) {
      setIsDirty(false);
    }
    return changedItems;
  }, [getValues('userRolesFormData'), initialDataRef.current]);

  const handleSubmitRoleForm = async () => {
    if (!isDirty) {
      return;
    }
    const currentData = getValues('userRolesFormData');

    setToast('Updating users...', { autoHide: false });
    const payload = changedItems.map((item) => ({
      id: item.id,
      roles: item.roles,
      clinics: item.clinics,
      userStatus: item.userStatus,
    }));

    isSubmittingRef.current = true;
    try {
      const result = await postUpdateUserPermissions(payload);
      if (result) {
        // Update original snapshot to the latest state
        initialDataRef.current = JSON.parse(
          JSON.stringify(
            currentData.map(
              (item) => changedItems.find((c) => c.id === item.id) || item,
            ),
          ),
        );
        setIsDirty(false);
        setToast('Users updated!');
      } else {
        resetPermissionForm();
        setToast('Users update failed!');
      }
    } finally {
      isSubmittingRef.current = false;
    }
  };

  const rowEvents = {
    onClick: (e, row, rowIndex) => {
      setActiveIndex(rowIndex);
      setShowConfirmModal(true);
      activeId.current = row.id;
    },
  };

  const columns = useMemo(() => {
    const tenancyRolesColumn = Object.keys(roleResults?.tenancy_roles).reduce(
      (acc, objectKey, currentIndex) => {
        return [
          ...acc,
          {
            dataField: `roles.${objectKey}`,
            text: roleResults?.tenancy_roles[objectKey].name,
            classes: 'col6',
            formatter: managerUserCheckboxFormatter,
            headerFormatter: (column) =>
              managerUserRoleHeaderFormatter(
                column,
                currentIndex,
                calculateMiddleIndex(
                  Object.keys(roleResults?.tenancy_roles).length,
                ),
              ),
            headerClasses: 'user-role-header text-center',
            events: {
              onClick: (e, column, columnIndex, row, rowIndex) => {
                // Only stop propagation to avoid row click
                e.stopPropagation();
              },
            },
            formatExtraData: {
              onToggle: (rowIndex) => {
                const globalIndex = (currentPage - 1) * pageSize + rowIndex;
                const currentValue = getValues('userRolesFormData');
                const fieldToUpdate = objectKey;
                const toUpdateValue =
                  !currentValue[globalIndex].roles[fieldToUpdate];
                const newArr = [...currentValue];
                newArr[globalIndex] = {
                  ...newArr[globalIndex],
                  roles: {
                    ...newArr[globalIndex].roles,
                    [fieldToUpdate]: toUpdateValue,
                  },
                };
                setValue('userRolesFormData', newArr);
                setIsDirty(true);
              },
            },
          },
        ];
      },
      [],
    );

    const middleClinicsIndex = calculateMiddleIndex(clinics.length);
    const tenancyClinicsColumn = clinics.map((clinic, clinicIndex) => ({
      dataField: `clinics.${clinic.clinic_id}`,
      text: clinic.clinic,
      classes: 'col6',
      formatter: managerUserCheckboxFormatter,
      headerFormatter: (column) =>
        managerUserClinicHeaderFormatter(
          column,
          clinicIndex,
          middleClinicsIndex,
        ),
      headerClasses: 'user-role-header text-center',
      events: {
        onClick: (e, column, columnIndex, row, rowIndex) => {
          // Only stop propagation to avoid row click
          e.stopPropagation();
        },
      },
      formatExtraData: {
        onToggle: (rowIndex) => {
          const globalIndex = (currentPage - 1) * pageSize + rowIndex;
          const currentValue = getValues('userRolesFormData');
          const fieldToUpdate = String(clinic.clinic_id);
          const toUpdateValue =
            !currentValue[globalIndex].clinics?.[fieldToUpdate];
          const newArr = [...currentValue];
          const currentClinics = newArr[globalIndex].clinics || {};
          newArr[globalIndex] = {
            ...newArr[globalIndex],
            clinics: {
              ...currentClinics,
              [fieldToUpdate]: toUpdateValue,
            },
          };
          setValue('userRolesFormData', newArr);
          setIsDirty(true);
        },
      },
    }));

    const verifiedColumn = {
      dataField: 'userStatus',
      text: 'Verified',
      classes: 'col3',
      formatter: managerUserCheckboxFormatter,
      headerClasses: 'user-normal-header',
      events: {
        onClick: (e, column, columnIndex, row, rowIndex) => {
          // Only stop propagation to avoid row click
          e.stopPropagation();
        },
      },
      formatExtraData: {
        onToggle: (rowIndex) => {
          const globalIndex = (currentPage - 1) * pageSize + rowIndex;
          const currentValue = getValues('userRolesFormData');
          const toUpdateValue =
            currentValue[globalIndex].userStatus == 1 ? 0 : 1;
          const newArr = [...currentValue];
          newArr[globalIndex] = {
            ...newArr[globalIndex],
            userStatus: toUpdateValue,
          };
          setValue('userRolesFormData', newArr);
          setIsDirty(true);
        },
      },
    };

    const blankPlaceholder = {
      dataField: '',
      text: '',
      classes: 'col2',
      formatter: () => <></>,
    };

    return [
      ...defaultColumns,
      verifiedColumn,
      ...tenancyRolesColumn,
      blankPlaceholder,
      ...tenancyClinicsColumn,
      ...deleteColumn,
    ];
  }, [roleResults, usersData, currentPage, pageSize]);

  return (
    <>
      {!isGetting ? (
        <form
          onSubmit={handleSubmit(handleSubmitRoleForm)}
          className={isDirty ? 'offset-actions-bar' : ''}
        >
          <div style={{ position: 'relative' }}>
            <BootstrapTable
              bootstrap4
              keyField="id"
              wrapperClasses="table-responsive"
              id="manage-user-table"
              remote={{ pagination: true, filter: false, sort: true }}
              bordered={false}
              data={paginatedData}
              columns={columns}
              rowEvents={rowEvents}
              // rowClasses={rowClasses}
              sort={sort}
              // onTableChange={handleTableChange}
            />
            <PaginationFooter
              currentPage={currentPage}
              totalPage={totalPages}
              pageSize={pageSize}
              canNextPage={canNextPage}
              canPreviousPage={canPreviousPage}
              handleNextPage={handleNextPage}
              handlePreviousPage={handlePreviousPage}
              setPageSize={setPageSize}
            />
            {showConfirmModal && (
              <ConfirmationModal
                onCancel={() => {
                  setShowConfirmModal(false);
                }}
                onConfirm={() => {}}
                top={`${(activeIndex + 1) * 50}px`}
                right={'-90px'}
              />
            )}
            {isDirty ? (
              <div className="settings-page-actions settings-page-actions-right-0">
                <button
                  type="button"
                  className="cancel-change"
                  onClick={resetPermissionForm}
                  disabled={isLoading}
                >
                  Cancel changes
                </button>
                <button
                  type="submit"
                  className="save-change"
                  disabled={isLoading}
                >
                  Save changes
                </button>
              </div>
            ) : null}
          </div>
        </form>
      ) : (
        <LoadingSpinner showText="Loading users..." />
      )}
    </>
  );
};
